import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Divider, Drawer, Input, Radio, Tag, Typography } from "antd";
import { ArrowLeftOutlined, ThunderboltOutlined } from "@ant-design/icons";
import {
    AVISO_LEGAL,
    CONCEPTOS,
    LIGAS,
    NOMBRE_LEY,
    TIPO_LABEL,
    TRATAMIENTO,
    buscarConcepto,
    filtrarConceptos,
} from "./catalogoConceptosLey";
import { Codigo, Z_INDEX_GUIA, anchoDrawerGuia } from "../componentesGuia";
import {
    BANDERAS_APLICABLES,
    ETIQUETA_BANDERA,
    etiquetaFormula,
} from "../aplicarConceptoLey";

const { Text, Paragraph, Title, Link } = Typography;

const COLOR_TIPO = { 1: "green", 2: "volcano", 3: "geekblue" };

const etiquetaTratamiento = (clave) => TRATAMIENTO[clave] || TRATAMIENTO.na;

// ── Bloque de una ley: artículos, texto y liga al PDF oficial ────────────────
const BloqueLey = ({ ley, articulos, texto }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ marginBottom: 4 }}>
            <Text strong>{NOMBRE_LEY[ley]}</Text>
            {articulos && (
                <Tag style={{ marginLeft: 8 }} color="default">
                    {articulos}
                </Tag>
            )}
        </div>
        <Paragraph style={{ fontSize: 13, marginBottom: 4 }}>{texto}</Paragraph>
        <Link href={LIGAS[ley]} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
            Abrir la {NOMBRE_LEY[ley]} (PDF oficial)
        </Link>
    </div>
);

// ── Ficha de un concepto ─────────────────────────────────────────────────────
const Ficha = ({ concepto, onVolver, onAplicar }) => {
    const trat = etiquetaTratamiento(concepto.tratamientoIsr);
    const { captura, formulas, acumulados } = concepto;
    // Los conceptos sin fundamento LFT son renglones breves del catálogo SAT: sólo traen
    // resumen, así que no aplican los avisos que hablan de fórmulas.
    const esFichaCompleta = !!concepto.lft;

    return (
        <>
            <Link onClick={onVolver} style={{ fontSize: 12 }}>
                <ArrowLeftOutlined /> Ver todos los conceptos
            </Link>

            <Title level={5} style={{ marginTop: 10, marginBottom: 6 }}>
                {concepto.nombre}
            </Title>
            <div style={{ marginBottom: 12 }}>
                <Tag color={COLOR_TIPO[concepto.tipo]}>{TIPO_LABEL[concepto.tipo]}</Tag>
                <Tag>Clave SAT {concepto.clave}</Tag>
                <Tag color={trat.color}>{trat.texto}</Tag>
            </div>

            <Alert type="info" showIcon message={concepto.resumen} style={{ marginBottom: 12 }} />

            {(concepto.avisos || []).map((a, i) => (
                <Alert key={i} type={a.tipo} showIcon message={a.texto} style={{ marginBottom: 12 }} />
            ))}

            {esFichaCompleta && !concepto.implementado && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="El asistente de configuración no crea este concepto"
                    description={
                        "Hay que darlo de alta a mano. Las fórmulas de abajo, si las hay, son una " +
                        "propuesta que tienes que revisar antes de usarla: no son las que el " +
                        "sistema siembra."
                    }
                />
            )}

            {!esFichaCompleta && (
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Este concepto todavía no tiene ficha a detalle"
                    description={
                        "Está en el catálogo para que lo encuentres y sepas cómo se trata, pero " +
                        "aún no documentamos su fundamento legal ni sus fórmulas. Si lo necesitas " +
                        "a fondo, pídelo a soporte."
                    }
                />
            )}

            {concepto.bolsaSeparacion && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Exención en bolsa compartida"
                    description={
                        "Los 90 UMA por año de servicio son una sola bolsa entre Indemnizaciones " +
                        "(025), Pagos por separación (023) y Prima por antigüedad (022). Cada " +
                        "concepto exenta sólo el remanente que dejaron los anteriores, y el orden " +
                        "lo decide el número del concepto. Si reordenas esos tres o intercalas " +
                        "algo entre ellos, la exención se reparte distinto."
                    }
                />
            )}

            {concepto.lft && (
                <>
                    <Divider orientation="left" plain>
                        Qué dice la Ley Federal del Trabajo
                    </Divider>
                    <BloqueLey ley="LFT" articulos={concepto.lft.articulos} texto={concepto.lft.texto} />
                </>
            )}

            {(concepto.isr || concepto.imss) && (
                <>
                    <Divider orientation="left" plain>
                        Cómo grava y qué exenta
                    </Divider>
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                        La exención no la fija la LFT: viene de la Ley del ISR. Lo que integra al
                        salario base de cotización lo fija la Ley del Seguro Social, y no siempre
                        coincide con lo que grava de ISR.
                    </Paragraph>
                    {concepto.isr && (
                        <BloqueLey
                            ley="LISR"
                            articulos={concepto.isr.articulos}
                            texto={concepto.isr.texto}
                        />
                    )}
                    {concepto.imss && (
                        <BloqueLey
                            ley="LSS"
                            articulos={concepto.imss.articulos}
                            texto={concepto.imss.texto}
                        />
                    )}
                </>
            )}

            {(captura || formulas) && (
                <>
                    <Divider orientation="left" plain>
                        Cómo capturarlo en Biwo
                    </Divider>

                    {onAplicar && formulas && formulas.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                            <Button
                                type="primary"
                                size="small"
                                icon={<ThunderboltOutlined />}
                                onClick={() => onAplicar(concepto, formulas, true)}
                            >
                                {concepto.implementado
                                    ? "Aplicar todo al concepto"
                                    : "Aplicar la propuesta al concepto"}
                            </Button>
                            <Paragraph
                                type="secondary"
                                style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}
                            >
                                Escribe las fórmulas, los nombres de cada importe, sus acumulados
                                fiscales y las banderas que apliquen. Sólo llena el formulario:
                                nada se guarda hasta que le des a Guardar.
                            </Paragraph>
                        </div>
                    )}

                    <div style={{ marginBottom: 10 }}>
                        <Text strong style={{ fontSize: 12 }}>
                            Tipo:{" "}
                        </Text>
                        <Tag color={COLOR_TIPO[concepto.tipo]}>{TIPO_LABEL[concepto.tipo]}</Tag>
                        <Text strong style={{ fontSize: 12 }}>
                            Tipo Concepto SAT:{" "}
                        </Text>
                        <Tag>{concepto.clave}</Tag>
                    </div>

                    {captura && captura.banderas && captura.banderas.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                            <div style={{ marginBottom: 4 }}>
                                <Text strong style={{ fontSize: 12 }}>
                                    Banderas que suele traer:{" "}
                                </Text>
                                {captura.banderas.map((b) => (
                                    <Tag
                                        key={b}
                                        color={BANDERAS_APLICABLES.includes(b) ? "purple" : "default"}
                                    >
                                        {ETIQUETA_BANDERA[b] || b}
                                    </Tag>
                                ))}
                            </div>
                            {/* Las grises son punteros únicos por empresa: el motor las resuelve
                                con find/findOne, así que prenderlas desde aquí haría que el
                                cálculo tomara el concepto equivocado sin avisar. */}
                            {captura.banderas.some((b) => !BANDERAS_APLICABLES.includes(b)) && (
                                <Paragraph
                                    type="secondary"
                                    style={{ fontSize: 12, marginBottom: 0 }}
                                >
                                    Las grises las asigna el sistema y no se aplican desde aquí:
                                    son marcas únicas por empresa, y sólo un concepto puede
                                    traerlas.
                                </Paragraph>
                            )}
                        </div>
                    )}

                    {captura && captura.notas && (
                        <ul style={{ paddingLeft: 18, fontSize: 13 }}>
                            {captura.notas.map((n, i) => (
                                <li key={i} style={{ marginBottom: 6 }}>
                                    {n}
                                </li>
                            ))}
                        </ul>
                    )}

                    {formulas && formulas.length > 0 && (
                        <>
                            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 10 }}>
                                {onAplicar
                                    ? "Usar escribe esa fórmula en su pestaña; el clic sobre la fórmula la copia al portapapeles."
                                    : "Da clic en cualquier fórmula para copiarla y pégala en la pestaña que indica la etiqueta."}
                            </Paragraph>
                            {formulas.map((f, i) => (
                                <div key={i} style={{ marginBottom: 12 }}>
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                        {etiquetaFormula(f)}
                                    </Tag>
                                    {onAplicar && (
                                        <Button
                                            type="link"
                                            size="small"
                                            style={{ padding: 0, fontSize: 12, height: "auto" }}
                                            onClick={() => onAplicar(concepto, [f], false)}
                                        >
                                            Usar
                                        </Button>
                                    )}
                                    <div style={{ marginTop: 6 }}>
                                        <Codigo>{f.formula}</Codigo>
                                    </div>
                                    {f.explicacion && (
                                        <Paragraph
                                            type="secondary"
                                            style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}
                                        >
                                            {f.explicacion}
                                        </Paragraph>
                                    )}
                                </div>
                            ))}
                        </>
                    )}

                    {acumulados && (
                        <>
                            <Text strong style={{ fontSize: 12 }}>
                                Acumulados fiscales que deben ir en cada pestaña:
                            </Text>
                            <div style={{ marginTop: 6 }}>
                                {[
                                    ["Importe Total", acumulados.total],
                                    ["Importe 1", acumulados.importe1],
                                    ["Importe 2", acumulados.importe2],
                                ]
                                    .filter(([, lista]) => lista && lista.length > 0)
                                    .map(([campo, lista]) => (
                                        <div key={campo} style={{ marginBottom: 4, fontSize: 12 }}>
                                            <Tag style={{ fontSize: 11 }}>{campo}</Tag>
                                            {lista.join(" · ")}
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
};

// ── Renglón de la lista ──────────────────────────────────────────────────────
const RenglonConcepto = ({ concepto, onClick }) => {
    const trat = etiquetaTratamiento(concepto.tratamientoIsr);
    return (
        <div
            onClick={onClick}
            style={{
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                padding: "8px 10px",
                marginBottom: 8,
                cursor: "pointer",
            }}
        >
            <div style={{ marginBottom: 2 }}>
                <Tag color={COLOR_TIPO[concepto.tipo]} style={{ fontSize: 11 }}>
                    {TIPO_LABEL[concepto.tipo]} {concepto.clave}
                </Tag>
                <Text strong style={{ fontSize: 13 }}>
                    {concepto.nombre}
                </Text>
                <Tag color={trat.color} style={{ fontSize: 11, marginLeft: 6 }}>
                    {trat.texto}
                </Tag>
            </div>
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.65)" }}>{concepto.resumen}</div>
        </div>
    );
};

/**
 * Guía legal de Conceptos de Nómina.
 *
 * Panel lateral de sólo lectura. Se abre desde el listado de Conceptos (modo catálogo,
 * con buscador) y desde el campo Tipo Concepto SAT del alta y la edición, ya posicionado
 * en la ficha de la clave que se está capturando.
 *
 * `claveSat` es el c_TipoPercepcion del catálogo SAT y `tipo` es 1 Percepción /
 * 2 Deducción / 3 Otro Pago. Los dos son necesarios: la clave se repite entre catálogos.
 *
 * `onAplicar(concepto, formulas, todo)` sólo lo mandan las dos pantallas que tienen un
 * Form donde escribir. Sin él no se pintan los botones de aplicar, que es lo que pasa
 * cuando la guía se abre desde el listado.
 */
const GuiaConceptosLey = ({ visible, claveSat, tipo, onClose, onAplicar }) => {
    const [seleccion, setSeleccion] = useState(null);
    const [busqueda, setBusqueda] = useState("");
    const [filtroTipo, setFiltroTipo] = useState(0);
    // Se pidió una ficha que todavía no existe en el catálogo: se abre el buscador y se avisa.
    const [sinFicha, setSinFicha] = useState(false);

    // Cada vez que se abre (o cambia la clave del formulario) se recoloca la guía.
    useEffect(() => {
        if (!visible) return;
        const ficha = buscarConcepto(claveSat, tipo);
        setSeleccion(ficha);
        setSinFicha(!!claveSat && !!tipo && !ficha);
        setBusqueda("");
        setFiltroTipo(claveSat && tipo && !ficha ? Number(tipo) : 0);
    }, [visible, claveSat, tipo]);

    const resultados = useMemo(
        () => filtrarConceptos(busqueda, filtroTipo || undefined),
        [busqueda, filtroTipo]
    );

    const volver = () => {
        setSeleccion(null);
        setSinFicha(false);
    };

    // Al entrar a una ficha desde el fondo de la lista, el Drawer conserva el scroll y la
    // ficha abre a media altura. Se sube al inicio del contenido, sin tocar el DOM por fuera.
    const tope = useRef(null);
    useEffect(() => {
        if (tope.current) tope.current.scrollIntoView({ block: "start" });
    }, [seleccion]);

    return (
        <Drawer
            title="Guía legal de conceptos"
            placement="right"
            width={anchoDrawerGuia()}
            visible={visible}
            onClose={onClose}
            zIndex={Z_INDEX_GUIA}
            bodyStyle={{ paddingTop: 12 }}
        >
            <div ref={tope} />
            {seleccion ? (
                <Ficha concepto={seleccion} onVolver={volver} onAplicar={onAplicar} />
            ) : (
                <>
                    <Paragraph style={{ marginBottom: 10 }}>
                        Busca el concepto que vas a capturar y te dice qué es según la Ley Federal
                        del Trabajo, si grava o exenta, y con qué fórmulas darlo de alta
                        {onAplicar ? ", que puedes aplicar directo al concepto." : "."}
                    </Paragraph>

                    {sinFicha && (
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 10 }}
                            message={`Todavía no hay ficha para la clave ${claveSat} de ${(
                                TIPO_LABEL[tipo] || ""
                            ).toLowerCase()}.`}
                            description="Busca aquí abajo el concepto que te interese."
                        />
                    )}

                    <Input.Search
                        allowClear
                        placeholder="Buscar un concepto (ej: gratificacion, horas extra, infonavit)"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                        style={{ marginBottom: 10 }}
                    />

                    <Radio.Group
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                        buttonStyle="solid"
                        size="small"
                        style={{ marginBottom: 10 }}
                    >
                        <Radio.Button value={0}>Todos</Radio.Button>
                        <Radio.Button value={1}>Percepciones</Radio.Button>
                        <Radio.Button value={2}>Deducciones</Radio.Button>
                        <Radio.Button value={3}>Otros pagos</Radio.Button>
                    </Radio.Group>

                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                        {busqueda.trim()
                            ? `${resultados.length} de ${CONCEPTOS.length} conceptos coinciden con "${busqueda}".`
                            : `${resultados.length} conceptos. Da clic en cualquiera para ver su ficha.`}
                    </Paragraph>

                    {resultados.length === 0 ? (
                        <Alert
                            type="info"
                            showIcon
                            message="Ningún concepto coincide con esa búsqueda."
                            description="Prueba con una palabra sola, como aguinaldo, prima, vales o infonavit."
                        />
                    ) : (
                        resultados.map((c) => (
                            <RenglonConcepto
                                key={`${c.tipo}-${c.clave}`}
                                concepto={c}
                                onClick={() => setSeleccion(c)}
                            />
                        ))
                    )}
                </>
            )}

            <Divider style={{ marginTop: 20, marginBottom: 8 }} />
            <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 0 }}>
                {AVISO_LEGAL}
            </Paragraph>
        </Drawer>
    );
};

export default GuiaConceptosLey;
